import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { clearToken } from "@/lib/authToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Phase B (2026-02-27): cookie-only auth.
//   * `withCredentials = true` makes every axios call send the `tm_auth`
//     httpOnly cookie set by the backend on login + magic-link consume.
//   * The legacy `Authorization: Bearer` axios interceptor has been removed —
//     the cookie is the SOLE auth path on the wire. XSS can't read it.
//   * `authToken.js` getToken/setToken are now no-ops; we still call
//     `clearToken()` on logout so any stale localStorage entry is purged.
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState(null); // null = unknown, [] = none
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    // Platform admin = Toothrocket-itself admin. A tenant lab can have its own
    // superadmin without being a platform admin. Used to gate MRR / Toothmatrix tabs.
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    // "View as another role" — superadmin-only preview mode. Stored in
    // sessionStorage so a page reload keeps the preview, but it never persists
    // across browser sessions (preventing accidental sticky lock-out).
    const [previewRole, setPreviewRoleState] = useState(() => {
        try { return sessionStorage.getItem("tr_preview_role") || null; } catch { return null; }
    });
    const [rolePresetsCache, setRolePresetsCache] = useState(null);

    // Authenticated session check — cookie is auto-attached. A 401 simply
    // means "no session", not an error.
    const refresh = useCallback(async () => {
        try {
            const [meRes, permRes] = await Promise.allSettled([
                axios.get(`${API}/auth/me`),
                axios.get(`${API}/me/permissions`),
            ]);
            if (meRes.status === "rejected") {
                // Not authenticated — drop any cached UI state silently.
                setUser(null);
                setPermissions(null);
                setIsSuperadmin(false);
                setIsPlatformAdmin(false);
                return null;
            }
            setUser(meRes.value.data);
            if (permRes.status === "fulfilled") {
                setPermissions(permRes.value.data.permissions || []);
                setIsSuperadmin(!!permRes.value.data.is_superadmin);
                setIsPlatformAdmin(!!permRes.value.data.is_platform_admin);
            } else {
                // Clients (role=client) don't have admin permissions — that's fine.
                setPermissions([]);
                setIsSuperadmin(false);
                setIsPlatformAdmin(false);
            }
            return meRes.value.data;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Phase B one-time migration: wipe any `tr_token` left behind in
        // localStorage from the Phase-A dual-path window. The cookie is the
        // sole auth source now; removing the legacy entry closes the XSS-
        // exfil window for users who never logged out between phases.
        clearToken();
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password, remember = false) => {
        // Backend sets the httpOnly cookie on the response and returns the user
        // object. We no longer read or cache the `token` field — the cookie is
        // the sole auth artefact for subsequent requests.
        // `remember=true` opts the user into the 30-day trusted-device cookie
        // (`tr_trusted_device`) — backend will mint it server-side and the
        // Login screen's silent /trusted-device/exchange call will auto-login
        // on subsequent visits until the device expires or is revoked.
        const { data } = await axios.post(`${API}/auth/login`, { email, password, remember: !!remember });
        // Iter 82e — Always clear any stale "view-as" preview from a previous
        // session on a fresh login. Without this, a user who selected
        // "view as: finance" two visits ago would still see only the Finance
        // tab on every subsequent login (sessionStorage survives page
        // reloads, and the previous render-cycle's reactive cleanup may not
        // run before the new login lands).
        try { sessionStorage.removeItem("tr_preview_role"); } catch { /* noop */ }
        setPreviewRoleState(null);
        setUser(data.user);
        // Fire-and-forget permission refresh; UI will gate once it lands.
        axios.get(`${API}/me/permissions`).then((r) => {
            setPermissions(r.data.permissions || []);
            setIsSuperadmin(!!r.data.is_superadmin);
            setIsPlatformAdmin(!!r.data.is_platform_admin);
        }).catch((err) => {
            // Client logins have no admin permissions — non-error case.
            if (err?.response?.status !== 403) {
                console.warn("[auth] /me/permissions after login failed:", err?.message);
            }
            setPermissions([]);
            setIsSuperadmin(false);
            setIsPlatformAdmin(false);
        });
        return data.user;
    }, []);

    /**
     * Verify a 6-digit email-OTP code (iter 71). Identical post-success
     * shape to `login` above — backend sets the httpOnly cookie + returns
     * `{token, user, next}`. `next` lets the caller deep-link the user to
     * the page they were trying to reach when the session expired.
     */
    const verifyOtp = useCallback(async (email, code, rememberDevice = false) => {
        const { data } = await axios.post(`${API}/auth/otp/verify`, { email, code, remember_device: !!rememberDevice });
        setUser(data.user);
        axios.get(`${API}/me/permissions`).then((r) => {
            setPermissions(r.data.permissions || []);
            setIsSuperadmin(!!r.data.is_superadmin);
            setIsPlatformAdmin(!!r.data.is_platform_admin);
        }).catch((err) => {
            if (err?.response?.status !== 403) {
                console.warn("[auth] /me/permissions after OTP verify failed:", err?.message);
            }
            setPermissions([]);
            setIsSuperadmin(false);
            setIsPlatformAdmin(false);
        });
        return { user: data.user, next: data.next };
    }, []);

    const logout = useCallback(() => {
        // Server clears the httpOnly cookie. If the network is down, the
        // cookie will simply expire on its own — the UI state below is the
        // user-visible "logged out" signal.
        axios.post(`${API}/auth/logout`).catch((err) => {
            console.warn("[auth] logout endpoint failed (cookie may persist briefly):", err?.message);
        });
        clearToken(); // wipes any legacy `tr_token` left in localStorage from Phase A.
        // Iter 82e — also drop any "view-as" preview state so the NEXT user
        // who logs in on this device doesn't inherit a previous user's
        // role-preview (sessionStorage persists until tab close, which is
        // explicitly NOT what we want across logout/login boundaries).
        try { sessionStorage.removeItem("tr_preview_role"); } catch { /* noop */ }
        setPreviewRoleState(null);
        setUser(null);
        setPermissions(null);
        setIsSuperadmin(false);
    }, []);

    const setPreviewRole = useCallback(async (role) => {
        if (!role) {
            setPreviewRoleState(null);
            try { sessionStorage.removeItem("tr_preview_role"); }
            catch (err) { console.warn("[auth] preview-role clear failed:", err?.message); }
            return;
        }
        // Fetch the role presets on demand if we don't have them cached
        if (!rolePresetsCache) {
            try {
                const { data } = await axios.get(`${API}/admin/permissions/registry`);
                setRolePresetsCache(data);
            } catch (err) {
                console.warn("[auth] permissions registry fetch failed:", err?.message);
                return;
            }
        }
        setPreviewRoleState(role);
        try { sessionStorage.setItem("tr_preview_role", role); }
        catch (err) { console.warn("[auth] preview-role save failed:", err?.message); }
    }, [rolePresetsCache]);

    const hasPerm = useCallback(
        (key) => {
            // Iter 82e — Defense in depth: any user whose JWT/cookie role is
            // literally "superadmin" gets everything, regardless of what the
            // `/me/permissions` response said about `is_superadmin`. This
            // closes a race where the user object loaded but the permissions
            // response is still in flight, AND a defensive guard against any
            // future bug where the route returns is_superadmin=false for an
            // actual superadmin (the user's role is the source of truth).
            //
            // Note this short-circuit deliberately runs BEFORE the
            // previewRole check — when a superadmin is previewing as another
            // role, the OTHER guard below ("if previewRole && ...") still
            // gates them through the preset, but only when the cache exists.
            // If the cache hasn't loaded yet, we still want the real
            // superadmin to see their full UI rather than be stuck looking
            // at a half-rendered view.
            const isRoleSuperadmin = (user?.role || "").toLowerCase() === "superadmin";
            // When previewing a role, gate by that role's presets — regardless of actual perms.
            if (previewRole && rolePresetsCache) {
                const r = rolePresetsCache.roles?.find((x) => x.key === previewRole);
                return r ? (r.presets || []).includes(key) : false;
            }
            if (isSuperadmin || isRoleSuperadmin) return true;
            if (!Array.isArray(permissions)) return false;
            return permissions.includes(key);
        },
        [previewRole, rolePresetsCache, permissions, isSuperadmin, user?.role],
    );

    // Split the context value into two memoised halves so consumers that only
    // care about `user` don't re-render when `previewRole` flips, and vice-versa.
    const authActions = useMemo(
        () => ({ login, logout, refresh, setPreviewRole, verifyOtp }),
        [login, logout, refresh, setPreviewRole, verifyOtp],
    );
    const authState = useMemo(
        () => ({ user, loading, permissions, isSuperadmin, isPlatformAdmin, previewRole, hasPerm }),
        [user, loading, permissions, isSuperadmin, isPlatformAdmin, previewRole, hasPerm],
    );
    const value = useMemo(
        () => ({ ...authState, ...authActions }),
        [authState, authActions],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth outside provider");
    return ctx;
}
